import React, { useState, useEffect, useCallback } from 'react';
import SearchBar from './components/SearchBar';
import MovieList from './components/MovieList';
import MovieDetail from './components/MovieDetail';
import Loader from './components/Loader';
import Pagination from './components/Pagination';
import Favorites from './components/Favorites';
import { searchMovies, getMovieDetails } from './services/api';
import { FaHeart, FaFire, FaHistory, FaExclamationTriangle, FaArrowUp, FaFilm, FaTv, FaPlayCircle } from 'react-icons/fa';

const TRENDING_SUGGESTIONS = [
  { label: 'Inception', icon: <FaFire /> },
  { label: 'The Dark Knight', icon: <FaFire /> },
  { label: 'Interstellar', icon: <FaFire /> },
  { label: 'Avengers', icon: <FaFire /> },
  { label: 'Stranger Things', icon: <FaFire /> },
  { label: 'Breaking Bad', icon: <FaFire /> },
  { label: 'Oppenheimer', icon: <FaFire /> },
  { label: 'Dune', icon: <FaFire /> },
];

const TYPE_FILTERS = [
  { value: '', label: 'All', icon: null },
  { value: 'movie', label: 'Movies', icon: <FaFilm size={12} /> },
  { value: 'series', label: 'Series', icon: <FaTv size={12} /> },
  { value: 'episode', label: 'Episodes', icon: <FaPlayCircle size={12} /> },
];

function App() {
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [movieDetails, setMovieDetails] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [favorites, setFavorites] = useState([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [searchHistory, setSearchHistory] = useState([]);
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Load favorites and search history from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem('movieFavorites');
    if (savedFavorites) setFavorites(JSON.parse(savedFavorites));

    const savedHistory = localStorage.getItem('searchHistory');
    if (savedHistory) setSearchHistory(JSON.parse(savedHistory));
  }, []);

  // Save favorites
  useEffect(() => {
    localStorage.setItem('movieFavorites', JSON.stringify(favorites));
  }, [favorites]);

  // Save search history
  useEffect(() => {
    localStorage.setItem('searchHistory', JSON.stringify(searchHistory));
  }, [searchHistory]);

  // Back to top visibility
  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const addToHistory = (query) => {
    setSearchHistory(prev => {
      const filtered = prev.filter(h => h.toLowerCase() !== query.toLowerCase());
      return [query, ...filtered].slice(0, 8);
    });
  };

  const fetchMovies = useCallback(async (query, page, type = typeFilter) => {
    if (!query) return;

    setLoading(true);
    setError(null);

    try {
      const data = await searchMovies(query, page, type);

      if (data.Response === 'True') {
        setMovies(data.Search);
        setTotalResults(parseInt(data.totalResults, 10));
      } else {
        setMovies([]);
        setError(data.Error);
        setTotalResults(0);
      }
    } catch (err) {
      setError('Failed to fetch movies. Please try again.');
      setMovies([]);
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  const handleSearch = (query) => {
    setSearchTerm(query);
    setCurrentPage(1);
    addToHistory(query);
    fetchMovies(query, 1, typeFilter);
  };

  const handleTypeFilter = (type) => {
    setTypeFilter(type);
    if (searchTerm) {
      setCurrentPage(1);
      fetchMovies(searchTerm, 1, type);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchMovies(searchTerm, page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fetchMovieDetails = async (imdbID) => {
    setDetailLoading(true);

    try {
      const data = await getMovieDetails(imdbID);
      if (data.Response === 'True') {
        setMovieDetails(data);
      } else {
        setError(data.Error);
      }
    } catch (err) {
      setError('Failed to fetch movie details. Please try again.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSelectMovie = (imdbID) => {
    setSelectedMovie(imdbID);
    fetchMovieDetails(imdbID);
  };

  const handleCloseDetails = () => {
    setSelectedMovie(null);
    setMovieDetails(null);
  };

  const handleToggleFavorite = (movie) => {
    setFavorites(prev => {
      const exists = prev.some(fav => fav.imdbID === movie.imdbID);
      return exists
        ? prev.filter(fav => fav.imdbID !== movie.imdbID)
        : [...prev, movie];
    });
  };

  const isMovieInFavorites = (imdbID) => favorites.some(fav => fav.imdbID === imdbID);

  const hasResults = searchTerm !== '' && !loading;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>
      {/* Navbar */}
      <nav className="cine-navbar">
        <div className="cine-navbar-inner">
          <div className="cine-logo">Giselle's Movie Search</div>
          <div className="nav-actions">
            {favorites.length > 0 && (
              <button
                className="nav-link-custom"
                onClick={() => document.querySelector('.favorites-section')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <FaHeart style={{ color: 'var(--accent-red)' }} />
                <span className="nav-fav-badge">{favorites.length}</span>
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="cine-hero">
        <div className="hero-content">
          <h1 className="hero-title">
            Discover Your Next<br />
            <span className="highlight">Favorite Film</span>
          </h1>
          <p className="hero-subtitle">
            Explore thousands of movies, series, and episodes. Build your personal collection.
          </p>

          <SearchBar onSearch={handleSearch} isLoading={loading} />

          {/* Type Filter */}
          <div className="type-filter">
            {TYPE_FILTERS.map((filter) => (
              <button
                key={filter.value}
                className={`type-pill ${typeFilter === filter.value ? 'active' : ''}`}
                onClick={() => handleTypeFilter(filter.value)}
              >
                {filter.icon && <span style={{ marginRight: 4 }}>{filter.icon}</span>}
                {filter.label}
              </button>
            ))}
          </div>

          {/* Search History */}
          {searchHistory.length > 0 && !searchTerm && (
            <div className="search-history">
              {searchHistory.map((term) => (
                <button
                  key={term}
                  className="history-chip"
                  onClick={() => handleSearch(term)}
                >
                  <FaHistory className="chip-icon" size={10} />
                  {term}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Favorites */}
        {favorites.length > 0 && (
          <Favorites
            favorites={favorites}
            onSelectMovie={handleSelectMovie}
            onToggleFavorite={handleToggleFavorite}
          />
        )}

        {/* Error */}
        {error && !loading && (
          <div className="cine-error">
            <FaExclamationTriangle className="error-icon" />
            <span className="error-text">{error}</span>
          </div>
        )}

        {/* Trending Suggestions (when no search) */}
        {!searchTerm && !loading && (
          <div className="trending-section">
            <div className="section-header">
              <div className="section-title">
                <FaFire className="icon" />
                Trending Searches
              </div>
            </div>
            <div className="trending-tags">
              {TRENDING_SUGGESTIONS.map((item) => (
                <button
                  key={item.label}
                  className="trending-tag"
                  onClick={() => handleSearch(item.label)}
                >
                  <span className="tag-icon">{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results Info */}
        {hasResults && totalResults > 0 && (
          <div className="results-info">
            <h2>
              Results for <span className="query">"{searchTerm}"</span>
            </h2>
            <span className="results-count">
              Showing {(currentPage - 1) * 10 + 1}-{Math.min(currentPage * 10, totalResults)} of {totalResults}
            </span>
          </div>
        )}

        {hasResults && totalResults === 0 && !error && (
          <div className="results-info">
            <h2>No results for <span className="query">"{searchTerm}"</span></h2>
          </div>
        )}

        {/* Movie List / Loader */}
        {loading ? (
          <Loader />
        ) : (
          <>
            <MovieList
              movies={movies}
              onSelectMovie={handleSelectMovie}
              favorites={favorites}
              onToggleFavorite={handleToggleFavorite}
              searchPerformed={searchTerm !== ''}
            />

            {totalResults > 0 && (
              <Pagination
                currentPage={currentPage}
                totalResults={totalResults}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="cine-footer">
        <div className="main-content" style={{ padding: '0 1.5rem' }}>
          <p>Giselle's Movie Search &middot; Powered by OMDb API</p>
          <p>
            <a href="https://www.flaticon.com/free-icons/cinema" title="cinema icons" target="_blank" rel="noopener noreferrer">
              Cinema icons by fjstudio - Flaticon
            </a>
          </p>
        </div>
      </footer>

      {/* Back to Top */}
      <button
        className={`back-to-top ${showBackToTop ? 'visible' : ''}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Back to top"
      >
        <FaArrowUp />
      </button>

      {/* Movie Detail Modal */}
      {selectedMovie && movieDetails && !detailLoading && (
        <MovieDetail
          movie={movieDetails}
          onClose={handleCloseDetails}
          isFavorite={isMovieInFavorites(selectedMovie)}
          onToggleFavorite={handleToggleFavorite}
        />
      )}

      {/* Detail Loading Overlay */}
      {detailLoading && (
        <div className="modal-overlay">
          <div className="cine-loader">
            <div className="cine-loader-ring" />
            <div className="cine-loader-text">Loading details</div>
            <div className="cine-loader-dots">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
